import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  Home,
  CheckCircle,
  Star,
  MonitorSpeaker,
  Zap,
  Volume2,
  Phone
} from 'lucide-react';
import { Link } from 'react-router-dom';

const FittedMediaWallsPage = () => {
  const mediaWallStyles = [
    {
      name: 'Floating Media Wall',
      subtitle: 'Modern Minimalism',
      description: 'Wall-mounted units that appear to float, creating a clean modern aesthetic. Perfect for contemporary living rooms and bedrooms.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/1f59907ae67a0b23a0730199889a2d6abb2344f77542d7a8293a03674221ba95.png',
      features: [
        'Wall-mounted construction',
        'Hidden bracket systems',
        'Integrated cable management',
        'LED backlighting options',
        'Handleless design',
        'Custom TV mounting'
      ],
      priceRange: 'From £2,800',
      color: 'text-[#8CB89F]'
    },
    {
      name: 'Floor to Ceiling Media',
      subtitle: 'Statement Walls',
      description: 'Full-height media walls that transform entire walls into entertainment centers. Maximum storage with dramatic visual impact.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png',
      features: [
        'Full wall transformation',
        'Multiple storage zones',
        'Integrated sound systems',
        'Climate-controlled sections',
        'Display lighting zones',
        'Bespoke configurations'
      ],
      priceRange: 'From £4,200',
      color: 'text-purple-600'
    },
    {
      name: 'Corner Media Solutions',
      subtitle: 'Space Optimization',
      description: 'Clever corner units that maximize awkward spaces. Perfect for smaller rooms where every inch counts.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/7244dd71420ba78ea5eafb4e9e5b8337104c7b606cd79b52559728247fe1eaa5.png',
      features: [
        'Corner space utilization',
        'Curved or angled designs',
        'Swivel TV mounting',
        'Compact storage solutions',
        'Integrated lighting',
        'Space-saving design'
      ],
      priceRange: 'From £2,200',
      color: 'text-green-600'
    }
  ];

  const mediaWallBenefits = [
    {
      icon: MonitorSpeaker,
      title: 'Entertainment Central',
      description: 'Integrated solutions for all your entertainment equipment and media'
    },
    {
      icon: Zap,
      title: 'Cable Management',
      description: 'Hidden cable routing keeps your media wall clean and organized'
    },
    {
      icon: Volume2,
      title: 'Audio Integration',
      description: 'Built-in speaker housing and sound system accommodation'
    },
    {
      icon: Star,
      title: 'Visual Impact',
      description: 'Transform your room with a stunning architectural feature wall'
    }
  ];

  const mediaFeatures = [
    {
      category: 'TV Integration',
      features: ['Custom TV mounting', 'All screen sizes', 'Articulating arms', 'Height adjustment', 'Cable concealment', 'Viewing optimization']
    },
    {
      category: 'Audio Systems',
      features: ['Sound bar housing', 'Speaker integration', 'Subwoofer compartments', 'Amplifier storage', 'Acoustic considerations', 'Wireless solutions']
    },
    {
      category: 'Gaming & Consoles',
      features: ['Console compartments', 'Ventilation systems', 'Game storage', 'Controller charging', 'Cable management', 'Quick access panels']
    },
    {
      category: 'Smart Technology',
      features: ['Smart home integration', 'LED mood lighting', 'Motion sensors', 'Touch controls', 'Voice activation', 'App connectivity']
    }
  ];

  const finishShowcase = [
    { name: 'High Gloss White', description: 'Ultra-modern reflective finish', color: '#FFFFFF' },
    { name: 'Matt Charcoal', description: 'Sophisticated dark finish', color: '#36454F' },
    { name: 'Wood Grain Oak', description: 'Natural wood texture', color: '#DEB887' },
    { name: 'Concrete Effect', description: 'Industrial modern finish', color: '#A8A8A8' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="lg:flex items-center gap-12">
            <div className="lg:w-1/2 mb-8 lg:mb-0">
              <div className="flex items-center gap-2 mb-4">
                <MonitorSpeaker className="h-6 w-6" style={{color: '#8CB89F'}} />
                <Badge className="text-white" style={{backgroundColor: '#8CB89F'}}>Entertainment Central</Badge>
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Fitted Media Walls
                <span className="block" style={{color: '#8CB89F'}}>Entertainment Redefined</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                <strong>Aston Lily Joinery</strong> creates stunning fitted media walls that transform your entertainment experience. Integrated technology meets beautiful design.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="text-white hover:opacity-90 px-8 py-4 text-lg" style={{backgroundColor: '#8CB89F'}}>
                    <Calculator className="mr-2 h-5 w-5" />
                    Get Media Wall Quote
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="px-8 py-4 text-lg border-2"
                    style={{borderColor: '#8CB89F', color: '#8CB89F'}}
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Tech Integration Consult
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:w-1/2">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/1f59907ae67a0b23a0730199889a2d6abb2344f77542d7a8293a03674221ba95.png"
                alt="Fitted Media Walls"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16" style={{backgroundColor: '#8CB89F10'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Fitted Media Walls?</h2>
            <p className="text-xl text-gray-600">
              Transform your entertainment experience with integrated technology
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mediaWallBenefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <benefit.icon className="h-12 w-12 mx-auto mb-4" style={{color: '#8CB89F'}} />
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Media Wall Styles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Media Wall Styles</h2>
            <p className="text-xl text-gray-600">
              Choose the perfect configuration for your space
            </p>
          </div>
          
          <div className="space-y-20">
            {mediaWallStyles.map((style, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex items-center gap-12`}>
                <div className="lg:w-1/2 mb-8 lg:mb-0">
                  <img 
                    src={style.image} 
                    alt={style.name}
                    className="w-full h-96 object-cover rounded-2xl shadow-xl"
                  />
                </div>
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="outline" className={style.color}>{style.subtitle}</Badge>
                  </div>
                  
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">{style.name}</h3>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{style.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {style.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <Home className="h-5 w-5 text-[#8CB89F]" />
                      <span className="font-semibold text-lg">{style.priceRange}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Features */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Media Wall Features</h2>
            <p className="text-xl text-gray-600">
              Comprehensive technology integration solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mediaFeatures.map((category, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl">{category.category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-2">
                    {category.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3" style={{color: '#8CB89F'}} />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Finish Showcase */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Premium Finishes</h2>
            <p className="text-xl text-gray-600">
              Stunning finishes for your media wall
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {finishShowcase.map((finish, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div 
                    className="w-full h-24 rounded-lg mb-4 border"
                    style={{backgroundColor: finish.color}}
                  ></div>
                  <h4 className="font-semibold text-lg mb-2">{finish.name}</h4>
                  <p className="text-sm text-gray-600">{finish.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Create Your Entertainment Hub</h2>
          <p className="text-xl mb-8 opacity-90">
            Transform your living space with a stunning fitted media wall designed and installed by Aston Lily Joinery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Get Media Wall Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white px-8 py-4 text-lg"
              >
                <Phone className="mr-2 h-5 w-5" />
                Technology Integration Consult
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FittedMediaWallsPage;